// Small building blocks shared by the console's screens.

import clsx from "clsx";
import type { ReactNode } from "react";
import type { AdminApiError } from "../lib/api";
import { fT } from "../lib/format";
import type { Column } from "../lib/series";
import s from "./ui.module.css";

/** Kicker + display title whose last words are the italic accent. */
export function PageHeader({ kicker, before, accent, after }: { kicker: ReactNode; before?: ReactNode; accent: ReactNode; after?: ReactNode }) {
  return (
    <header className={s.pageHead}>
      <span className="kicker ink-mute">{kicker}</span>
      <h1 className={clsx("t-serif-lg", s.title)}>
        {before}
        <span className={s.accent}>{accent}</span>
        {after}
      </h1>
    </header>
  );
}

/** Kicker followed by a rule to the end of the line (as on Profile). */
export function SectionHeader({ label, tone, children }: { label: ReactNode; tone?: "alert"; children?: ReactNode }) {
  return (
    <div className={s.sectionHead}>
      <span className={clsx("kicker", !tone && "ink-mute")} style={tone ? { color: "var(--alert)" } : undefined}>
        {label}
      </span>
      <span className={s.rule} />
      {children}
    </div>
  );
}

export interface PillSpec {
  label: string;
  color: string;
}

export function Pill({ pill, large }: { pill: PillSpec; large?: boolean }) {
  return (
    <span className={clsx(s.pill, large && s.pillLg)} style={{ color: pill.color }}>
      {pill.label}
    </span>
  );
}

export function Pills({ pills, large }: { pills: PillSpec[]; large?: boolean }) {
  if (pills.length === 0) return null;
  return (
    <span className={s.pills}>
      {pills.map((p) => (
        <Pill key={p.label} pill={p} {...(large ? { large } : {})} />
      ))}
    </span>
  );
}

/** Banned · Unverified · Support access. */
export function statusPills(r: { banned: boolean; emailVerified: boolean; supportAccessUntil: string | null }): PillSpec[] {
  const out: PillSpec[] = [];
  if (r.banned) out.push({ label: "Banned", color: "var(--alert)" });
  if (!r.emailVerified) out.push({ label: "Unverified", color: "var(--caution)" });
  if (r.supportAccessUntil) out.push({ label: "Support access", color: "var(--gold)" });
  return out;
}

export function Tag({ label, color }: { label: string; color: string }) {
  return (
    <span className={s.tag} style={{ color }}>
      {label}
    </span>
  );
}

export function Card({ head, children, bodyClass }: { head?: ReactNode; children: ReactNode; bodyClass?: string | undefined }) {
  return (
    <section className={s.card}>
      {head && <div className={s.cardHead}>{head}</div>}
      <div className={clsx(s.cardBody, bodyClass)}>{children}</div>
    </section>
  );
}

/**
 * Column chart: one flex column per value on a shared baseline. Previous-
 * period values (when given) are a 2px gold tick at their height.
 */
export function Columns({
  cols,
  height,
  gap,
  color = "var(--ink-soft)",
  lastColor,
  titles,
  empty,
}: {
  cols: Column[];
  height: number;
  gap: number;
  color?: string;
  lastColor?: string;
  titles?: string[];
  empty?: ReactNode;
}) {
  const allZero = cols.every((c) => c.value === 0);
  return (
    <div className={s.cols} style={{ height, gap }}>
      {cols.map((c, i) => (
        <div key={i} className={s.col} title={titles?.[i]}>
          <div className={s.colFill} style={{ height: `${c.h}%`, background: lastColor && i === cols.length - 1 ? lastColor : color }} />
          {c.p !== null && c.p > 0 && <div className={s.colPrev} style={{ bottom: `${c.p}%` }} />}
        </div>
      ))}
      {allZero && empty && <div className={s.colsEmpty}>{empty}</div>}
    </div>
  );
}

export function LoadingState({ page }: { page: string }) {
  return (
    <div className={s.loading} aria-busy="true">
      <div className={s.loadingLine}>
        <span className="pa-spinner" style={{ width: 16, height: 16 }} />
        <span className="t-mono-sm ink-mute">Loading {page}…</span>
      </div>
      <div className={clsx(s.skeleton, s.skelTitle)} />
      <div className={s.skelTiles}>
        {[0, 1, 2, 3, 4, 5].map((k) => (
          <div key={k} className={s.skelTile}>
            <div className={clsx(s.skeleton, s.skelBar)} />
            <div className={clsx(s.skeleton, s.skelValue)} />
            <div className={clsx(s.skeleton, s.skelSpark)} />
          </div>
        ))}
      </div>
      <div className={clsx(s.card, s.skelChart)} />
      <div className={s.skelPair}>
        <div className={clsx(s.card, s.skelCard)} />
        <div className={clsx(s.card, s.skelCard)} />
      </div>
    </div>
  );
}

export function ErrorState({ page, error, onRetry, onFind }: { page: string; error: AdminApiError; onRetry: () => void; onFind: () => void }) {
  const explanation =
    error.status === 0
      ? "The admin API didn't answer. Runner accounts are not affected; this page just couldn't read them."
      : error.status >= 500
        ? "The server failed while reading the numbers. Runner accounts are not affected; this page just couldn't read them."
        : `The API said: ${error.message}.`;
  return (
    <div className={s.error}>
      <span className="kicker ink-mute">{page} · couldn’t load</span>
      <h1 className={clsx("t-serif-lg", s.errorTitle)}>
        The numbers didn’t <span className={s.accent}>arrive.</span>
      </h1>
      <p className={clsx("t-body-md ink-soft", s.errorBody)}>{explanation} Try again, or open a runner directly from search.</p>
      <div className={clsx("t-mono-xs ink-mute", s.requestLine)}>
        GET {error.route} · {error.status || "no response"}
        {error.requestId ? ` · ${error.requestId}` : ""} · {fT(error.at)}:{String(error.at.getSeconds()).padStart(2, "0")}
      </div>
      <div className={s.errorActions}>
        <button className="btn-primary" onClick={onRetry}>
          Try again
        </button>
        <button className="btn-secondary" onClick={onFind}>
          Find a runner
        </button>
      </div>
    </div>
  );
}

export function EmptyState({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <div className={s.empty}>
      <span className="t-serif-md">{title}</span>
      <span className={clsx("t-body-sm ink-mute", s.emptyBody)}>{body}</span>
      {action}
    </div>
  );
}

/** A row of headline numbers on the hairline grid. */
export function StatGrid({ stats, cols, valueSize = 26 }: { stats: Array<{ label: string; value: string; sub?: string }>; cols: number; valueSize?: number }) {
  return (
    <section className={s.stats} style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}>
      {stats.map((st) => (
        <div key={st.label} className={s.stat}>
          <span className="kicker ink-mute">{st.label}</span>
          <span className="pa-stat-value" style={{ fontSize: valueSize }}>
            {st.value}
          </span>
          {st.sub && <span className="t-mono-xs ink-mute">{st.sub}</span>}
        </div>
      ))}
    </section>
  );
}

/** Format tags with a ×n count: POSTER ×3 · CARDS · ICS. */
export function FormatPips({ pips }: { pips: Array<{ tag: string; count: number }> }) {
  return (
    <span className={s.pips}>
      {pips.map((p) => (
        <span key={p.tag} className={s.pip}>
          {p.tag}
          {p.count > 1 && <span className="ink-mute"> ×{p.count}</span>}
        </span>
      ))}
    </span>
  );
}

/** A quiet caution callout (partial data, Analytics Engine unavailable). */
export function Note({ children }: { children: ReactNode }) {
  return <div className={clsx("ob-callout t-body-sm", s.callout)}>{children}</div>;
}
